import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, ThumbClickCommentBiEvent, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import CommentOutline from '@wix/da-ds/pkg/Icons/24x24/CommentOutline';
import { getDeviationResourceType } from '../../../DeviationViews/_helpers';
import ActionBarToggleButton from '../../_partials/items/ActionBarToggleButton';
import ActionBarButtonRestricted from '../../_partials/ActionBarButtonRestricted';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';

export interface Props {
  deviation: PapiDeviation;
  isOwner: boolean;
  actionBarType?: 'primary' | 'secondary';
  onFocusCommentEditor: (deviation: PapiDeviation) => void;
}

const CommentButton: React.FC<Props> = ({
  deviation,
  deviation: { extended },
  onFocusCommentEditor,
  isOwner,
  actionBarType: _actionBarType,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isSisuPopoverEnabled = useFlag('sisu_popover');

  const onClick = useCallback(() => {
    if (!extended) {
      return;
    }
    yieldToMain(() => {
      onFocusCommentEditor(deviation);
    });
  }, [extended, onFocusCommentEditor, deviation]);

  return (
    <ActionBarButtonRestricted
      loggedInOnly={isSisuPopoverEnabled}
      subscribersOnly
      restrictedActionName={isSisuPopoverEnabled ? ReferralType.COMMENT_ACTION : undefined}
      deviation={deviation}
      tooltip={isOwner ? t('actionbar.comment') : undefined}
      button={
        <ActionBarToggleButton
          onClick={onClick}
          icon={CommentOutline}
          label={isOwner || isMobile ? undefined : t('actionbar.comment.label')}
          biData={BiData<ThumbClickCommentBiEvent>({
            evid: 105,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            creatorid: deviation.author?.useridUuid ?? '',
            sectionname: BiContextProvidedValue,
            sectionid: BiContextProvidedValue,
            section_typeid: BiContextProvidedValue,
            sectiontype: BiContextProvidedValue,
            sectionrank: BiContextProvidedValue,
            rank: BiContextProvidedValue,
            sort_type: BiContextProvidedValue,
            reason_shown: BiContextProvidedValue,
            widgetname: BiContextProvidedValue,
          })}
        />
      }
    />
  );
};
CommentButton.displayName = 'CommentButton';

export default CommentButton;
